import * as React from 'react'
import { useSpring } from 'react-spring'
import { useMeasure } from 'react-use'

import { TypeStyleBodyMicro } from '@syconium/little-miss-figgy'

import { NextLink } from '../../../../../app/_components/navigation/NextLink'
import { PricingRange } from '../../../../../app/_components/pricing/PricingRange.client'
import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { useGoogleTagManager } from '../../../../../lib/hooks/useGoogleTagManager'

import { IProps } from './IProps'
import { ProductName } from './ProductName'
import {
  AutoCompleteResults,
  Container,
  ErrorContainer,
  ErrorMessage,
  PopularSearchItem,
  PopularSearchResults,
  PopularSearchTitle,
  Prices,
  ProductDetails,
  ProductTile,
  StyledResponsiveImage,
  imageSize,
} from './styles'

function noOp(): void {}

export const SearchOverlayResults: React.FC<IProps> = ({
  autoCompleteResults,
  hasError,
  onClearFormClick = noOp,
  onClickSearchOverlayProduct = noOp,
  popularSearchResults,
  popularSearchTitle,
  searchText,
}) => {
  const { search: searchCopy } = useTranslation()
  const { pushProductClick } = useGoogleTagManager()

  const [bind, { height }] = useMeasure<HTMLDivElement>()

  const containerSpring = useSpring({
    height: autoCompleteResults.length > 0 || popularSearchResults.length > 0 ? height : 0,
  })

  return (
    <>
      <Container style={containerSpring}>
        <div ref={bind}>
          {!hasError && (
            <>
              {autoCompleteResults.length > 0 && (
                <AutoCompleteResults aria-label='Search results'>
                  {autoCompleteResults.map((result, index) => {
                    return (
                      <ProductTile role='listitem' key={`${index}-${result.id}`}>
                        <NextLink
                          onClick={() => {
                            pushProductClick({
                              variant: {
                                category: result.categoryId,
                                currencyType: result.pricing.currencyCode,
                                externalParentId: result.defaultShopifyProductId,
                                discountPrice: result.pricing.discountPriceRange?.min ?? null,
                                handle: result.handle,
                                price: result.pricing.priceRange.min,
                                sku: result.defaultShopifyVariantSku,
                                shopifyId: result.defaultShopifyVariantId,
                              },
                              list: 'instant-search-results',
                              position: index,
                            })
                            onClickSearchOverlayProduct()
                          }}
                          href={result.url}
                        >
                          {result.imageSrc && (
                            <StyledResponsiveImage
                              alt={result.title}
                              src={result.imageSrc}
                              widths={{ unit: 'px', sm: imageSize, md: imageSize }}
                              aspectRatios={{ sm: 1, md: 1 }}
                              loading='default'
                            />
                          )}
                          <ProductDetails>
                            <ProductName allText={result.title} targetText={searchText} />
                            <Prices>
                              <TypeStyleBodyMicro color='primary'>
                                <PricingRange {...result.pricing} />
                              </TypeStyleBodyMicro>
                            </Prices>
                          </ProductDetails>
                        </NextLink>
                      </ProductTile>
                    )
                  })}
                </AutoCompleteResults>
              )}

              {popularSearchResults.length > 0 && (
                <PopularSearchResults>
                  <PopularSearchTitle>{popularSearchTitle}</PopularSearchTitle>
                  <ul>
                    {popularSearchResults.map((search, index) => (
                      <PopularSearchItem key={`${search}-${index}`}>
                        <NextLink href={`/search?q=${search}`} onClick={onClearFormClick}>
                          {search}
                        </NextLink>
                      </PopularSearchItem>
                    ))}
                  </ul>
                </PopularSearchResults>
              )}
            </>
          )}
        </div>
      </Container>
      {hasError && (
        <ErrorContainer>
          <ErrorMessage>{searchCopy.errorMessage}</ErrorMessage>
        </ErrorContainer>
      )}
    </>
  )
}
